import { useState, useEffect } from 'react';
import SaveButtonComponent from '../utilsComponents/SaveButtonComponent';
import { RemoveRowButtonComponent } from '../utilsComponents/RemoveRowButtonComponent';

const namePattern = /^\S[a-zA-Z-]+(?: \S[a-zA-Z-]+)*$/gm;

const BlackList = ({
  blackList,
  setBlackList,
  onSaveSettings,
  isValidated,
  isValidatedKg,
  loading,
}) => {
  const [messageError, setMessageError] = useState('');
  const [finder, setFinder] = useState('');
  const [visibleBlackList, setVisibleBlackList] = useState(blackList);

  useEffect(() => {
    const blackListCopy = blackList.slice();
    const filteredBlackList = blackListCopy.filter((item) =>
      item.name.toLowerCase().includes(finder.toLowerCase())
    );
    const sortedFiltered = filteredBlackList
      .slice()
      .sort((a, b) => b.enable - a.enable);
    setVisibleBlackList(sortedFiltered);
  }, [blackList, finder]);

  const onRemoveRow = (position, name) => {
    const newBlackList = blackList.filter((item) => item.name !== name.name);
    setFinder('');
    setBlackList(newBlackList);
  };

  const blackListOptionHandler = (itemName, value) => {
    const valueInt = +value;
    const item = blackList.find((entry) => entry.name === itemName);

    if (!item || item.enable === valueInt) return;

    const newValueBlackList = blackList.map((entry) =>
      entry.name === itemName ? { ...entry, enable: valueInt } : entry
    );

    setBlackList(newValueBlackList);
  };

  const addToBlacklistHandler = () => {
    const name = finder.trim();
    setMessageError('');

    if (!name) {
      setMessageError('Ingresá una marca o palabra');
      return;
    }

    if (!namePattern.test(name)) {
      setMessageError(
        'Usá solo letras y guiones; cada palabra debe empezar sin espacio inicial'
      );
      return;
    }

    if (blackList.some((item) => item.name.toLowerCase() === name.toLowerCase())) {
      setMessageError('Esa entrada ya está en la lista');
      return;
    }

    setBlackList([...blackList, { name, enable: 0, byuser: 1 }]);
    setFinder('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && showAddButton) {
      event.preventDefault();
      addToBlacklistHandler();
    }
  };

  const showAddButton =
    finder.trim().length > 0 && visibleBlackList.length === 0;

  const permittedCount = visibleBlackList.filter((item) => item.enable === 1)
    .length;

  return (
    <div className='flex flex-col gap-4 h-full min-h-0'>
      <p className='text-sm text-gray-600'>
        Marcas y palabras prohibidas en título o marca del catálogo de Mercado
        Libre. Los artículos que las contengan aparecerán en la pestaña
        &quot;Marcas prohibidas&quot; al cargar ASINs. Podés permitir excepciones
        por ítem del catálogo predefinido o agregar entradas propias.
      </p>

      <div className='flex flex-col gap-2 sm:flex-row sm:items-end'>
        <div className='flex-1'>
          <label
            htmlFor='blacklist-finder'
            className='block text-sm font-medium text-gray-700'
          >
            Buscar o agregar marca / palabra
          </label>
          <input
            id='blacklist-finder'
            type='text'
            name='finder'
            value={finder}
            placeholder='Ej: Nike'
            onChange={(e) => {
              setMessageError('');
              setFinder(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            className='mt-1 block w-full rounded-lg border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-300 sm:text-sm pl-3'
          />
        </div>
        {showAddButton && (
          <button
            type='button'
            onClick={addToBlacklistHandler}
            className='shrink-0 rounded-lg border border-primary-100 bg-primary-100 px-4 py-2 text-sm font-semibold text-primary-700 transition hover:border-primary-500'
          >
            Agregar
          </button>
        )}
      </div>

      {messageError && (
        <p className='text-sm text-red-600' role='alert'>
          {messageError}
        </p>
      )}

      <div className='flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 px-1'>
        <span>
          {visibleBlackList.length} entrada
          {visibleBlackList.length !== 1 ? 's' : ''}
          {finder.trim() ? ' (filtradas)' : ''}
        </span>
        <span className='text-gray-300'>|</span>
        <span className='text-primary-700'>
          {permittedCount} permitidas por el usuario
        </span>
        <span className='text-gray-300'>|</span>
        <span>{visibleBlackList.length - permittedCount} no permitidas</span>
      </div>

      <div
        data-testid='blacklist-panel'
        className='flex-1 min-h-0 overflow-hidden rounded-xl border border-gray-200 bg-gray-50/60 p-4'
      >
        {visibleBlackList.length > 0 ? (
          <ul className='max-h-[420px] overflow-y-auto flex flex-col gap-2'>
            {visibleBlackList.map((item, idx) => (
              <li
                key={item.name}
                className={`flex flex-col gap-2 rounded-lg border bg-white px-3 py-2.5 shadow-sm sm:flex-row sm:items-center sm:justify-between ${
                  item.enable === 1
                    ? 'border-primary-200 ring-1 ring-primary-100'
                    : 'border-gray-200'
                }`}
              >
                <span
                  className={`text-sm font-semibold ${
                    item.enable === 1 ? 'text-primary-800' : 'text-gray-800'
                  }`}
                >
                  {item.name}
                  {item.byuser === 1 && (
                    <span className='ml-2 text-xs font-normal text-gray-500'>
                      (agregada por vos)
                    </span>
                  )}
                </span>
                <div className='flex shrink-0 items-center gap-2'>
                  {!item?.byuser ? (
                    <select
                      id={`blackListOption${idx}`}
                      name={`blackListOption${idx}`}
                      autoComplete='blackListOption-name'
                      className='rounded-lg border-0 bg-white py-1.5 pl-2 pr-8 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-300'
                      value={item.enable}
                      onChange={(e) =>
                        blackListOptionHandler(item.name, e.target.value)
                      }
                    >
                      <option value={0}>No permitido</option>
                      <option value={1}>Permitido por el usuario</option>
                    </select>
                  ) : (
                    <RemoveRowButtonComponent
                      position={idx}
                      onRemoveRow={onRemoveRow}
                      iconTitle='Remover item'
                      name={item}
                    />
                  )}
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className='text-sm text-gray-500'>
            {finder.trim()
              ? 'No hay coincidencias. Podés agregar una entrada nueva con el botón Agregar.'
              : 'No hay marcas en la lista negra.'}
          </p>
        )}
      </div>

      <div className='w-full flex justify-end pt-1'>
        <SaveButtonComponent
          onSaveSettings={onSaveSettings}
          isValidated={isValidated}
          isValidatedKg={isValidatedKg}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default BlackList;
